import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getFirestore,
  query,
  orderBy,
} from "firebase/firestore";
import { useState, useEffect, useCallback, useMemo } from "react";
import { app } from "../firebase";

/**
 * Custom hook for performing CRUD operations on a 'favorites' subcollection
 * within a specific 'list' document in Firestore.
 * @param {string} listId The ID of the parent list document.
 */
export const useFavoritesCRUD = (listId) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const db = useMemo(() => getFirestore(app), [app]);

  const favoritesCollectionRef = useMemo(() => {
    if (!listId) {
      return null;
    }
    return query(
      collection(db, "lists", listId, "favorites"),
      orderBy("name", "asc"),
    );
  }, [db, listId]);

  const fetchFavorites = useCallback(async () => {
    if (!favoritesCollectionRef) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(favoritesCollectionRef);
      const fetchedFavorites = querySnapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));
      const sortedFavorites = fetchedFavorites.sort((a, b) =>
        a.name.localeCompare(b.name, "de"),
      );
      setFavorites(sortedFavorites);
    } catch (err) {
      console.error("Error fetching favorites:", err);
      setError(err.message || "Failed to fetch favorites.");
    } finally {
      setLoading(false);
    }
  }, [favoritesCollectionRef]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites, refetchTrigger]);

  const addFavorite = useCallback(
    async (newFavorite) => {
      if (!favoritesCollectionRef) {
        setError("List ID is not defined, cannot add favorite.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const plainCollectionRef = collection(db, "lists", listId, "favorites");
        await addDoc(plainCollectionRef, newFavorite);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error adding favorite:", err);
        setError(err.message || "Failed to add favorite.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId],
  );

  const updateFavorite = useCallback(
    async (id, updatedData) => {
      if (!listId) {
        setError("List ID is not defined, cannot update favorite.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const favoriteDocRef = doc(db, "lists", listId, "favorites", id);
        await updateDoc(favoriteDocRef, updatedData);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error updating favorite:", err);
        setError(err.message || "Failed to update favorite.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId],
  );

  const deleteFavorite = useCallback(
    async (id) => {
      if (!listId) {
        setError("List ID is not defined, cannot delete favorite.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const favoriteDocRef = doc(db, "lists", listId, "favorites", id);
        await deleteDoc(favoriteDocRef);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error deleting favorite:", err);
        setError(err.message || "Failed to delete favorite.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId],
  );

  return {
    favorites,
    loading,
    error,
    addFavorite,
    updateFavorite,
    deleteFavorite,
    refetchFavorites: () => setRefetchTrigger((prev) => prev + 1),
  };
};
